import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Section, SectionHeading } from './ui/Section';
import { Button } from './ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Contact = () => {
  const { t } = useLanguage();
  const [status, setStatus] = useState('idle'); // idle, sending, success, error

  // Dit unikke Formspree ID
  const FORMSPREE_ID = 'mdajvbbl';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    
    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData);

    try {
      const response = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
        // Gå tilbage til formularen efter 5 sekunder, hvis brugeren vil sende en til
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <Section id="contact" bg="bg-brand-dark">
      <div className="max-w-5xl mx-auto bg-brand-card rounded-2xl border border-brand-border p-8 md:p-16 relative overflow-hidden">
        {/* Dekorativ baggrundseffekt */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-brand-accent/5 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2"></div>
        
        <div className="grid lg:grid-cols-2 gap-16 relative z-10">
          <div>
            <SectionHeading className="!mb-6">{t.contact.heading}</SectionHeading>
            <p className="text-lg text-brand-textMuted leading-relaxed">
              {t.contact.body}
            </p>
          </div>

          <div>
            <AnimatePresence mode="wait">
              {status === 'success' ? (
                <motion.div 
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="bg-brand-accent/10 border border-brand-accent/30 rounded-xl p-10 text-center flex flex-col items-center justify-center min-h-[400px]"
                >
                  <div className="w-20 h-20 bg-brand-accent/20 rounded-full flex items-center justify-center mb-6">
                    <SafeIcon icon={FiCheckCircle} className="w-10 h-10 text-brand-accent" />
                  </div>
                  <h4 className="text-2xl font-bold text-white mb-3">Beskeden er modtaget!</h4>
                  <p className="text-brand-textMuted max-w-xs mx-auto">
                    {t.contact.success}
                  </p>
                </motion.div>
              ) : (
                <motion.form 
                  key="form"
                  onSubmit={handleSubmit} 
                  className="space-y-5"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="grid grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-brand-textMuted mb-2">{t.contact.form.name}</label>
                      <input 
                        type="text" 
                        name="name"
                        className="w-full bg-brand-dark border border-brand-border rounded-md px-4 py-3 text-white focus:outline-none focus:border-brand-accent transition-colors" 
                        required 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-textMuted mb-2">{t.contact.form.company}</label>
                      <input 
                        type="text" 
                        name="company"
                        className="w-full bg-brand-dark border border-brand-border rounded-md px-4 py-3 text-white focus:outline-none focus:border-brand-accent transition-colors" 
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-textMuted mb-2">{t.contact.form.email}</label>
                    <input 
                      type="email" 
                      name="email"
                      className="w-full bg-brand-dark border border-brand-border rounded-md px-4 py-3 text-white focus:outline-none focus:border-brand-accent transition-colors" 
                      required 
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-textMuted mb-2">{t.contact.form.message}</label>
                    <textarea 
                      name="message"
                      rows="4" 
                      className="w-full bg-brand-dark border border-brand-border rounded-md px-4 py-3 text-white focus:outline-none focus:border-brand-accent transition-colors resize-none" 
                      required
                    ></textarea>
                  </div>

                  {status === 'error' && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-start gap-3 text-red-400 text-sm bg-red-500/10 p-4 rounded-md border border-red-500/20"
                    >
                      <SafeIcon icon={FiAlertCircle} className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <p>{t.contact.error}</p>
                    </motion.div>
                  )}

                  <Button 
                    type="submit" 
                    variant="primary" 
                    className="w-full mt-2"
                    disabled={status === 'sending'}
                  >
                    {status === 'sending' ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t.contact.sending}
                      </span>
                    ) : t.contact.form.send}
                  </Button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default Contact;